// ./src/hooks/usePurchaseOrders.ts
//
// Hook for the account's purchase orders (restock orders to a vendor). Mirrors
// useInventoryItems: server pagination + load-more, race-guarded list, a
// concurrent-removal Set, and an optional eventId threaded as contextEventId (not
// stored) so orders can be managed from an event page. See em-scaffold-ui.
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";

import {
  PurchaseOrder,
  PurchaseOrderService,
  PurchaseOrderFilter,
  PurchaseOrderInput,
  Pagination,
  SortItem,
  listPage,
} from "@/utils/api";
import { upsertBy, upsertManyBy, removeBy, removeManyBy } from "@/utils";
import { useApp } from "@/contexts";
import { DEFAULT_PAGE_SIZE } from "@/config";
import { useRefreshOnWriteTimeout } from "./useRefreshOnWriteTimeout";

export interface UsePurchaseOrdersOptions {
  pageSize?: number;
  initialFilters?: Partial<PurchaseOrderFilter>;
  initialSort?: SortItem[];
  autoLoad?: boolean;
  /** When managed from an EVENT page, threads eventId as contextEventId (not stored) into every op. */
  eventId?: string | null;
}

export interface UsePurchaseOrdersReturn {
  purchaseOrder: PurchaseOrder | null;
  purchaseOrders: PurchaseOrder[];
  loading: boolean;
  isLoaded: boolean;
  isEmpty: boolean;

  loadingMore: boolean;
  hasMore: boolean;
  currentPage: number;
  pageSize: number;

  isCreating: boolean;
  isUpdating: boolean;
  removingIds: Set<string>;
  isRemoving: (id?: string) => boolean;

  filters: Partial<PurchaseOrderFilter>;
  sortBy: SortItem[];

  get: (id: string) => Promise<OpResult>;
  list: (
    filter?: Partial<PurchaseOrderFilter>,
    pagination?: Pagination,
    sortBy?: SortItem[],
    shouldReset?: boolean
  ) => Promise<OpResult>;
  create: (params: PurchaseOrderInput) => Promise<OpResult>;
  update: (id: string, params: PurchaseOrderInput) => Promise<OpResult>;
  remove: (id: string) => Promise<OpResult>;
  removeMany: (ids: string[]) => Promise<OpResult>;

  loadMore: () => Promise<void>;
  refresh: () => Promise<OpResult>;

  reset: () => void;
  getById: (id: string | undefined) => PurchaseOrder | undefined;

  totalCount: number;

  lastError: OpResult | null;
  clearError: () => void;
}

const DEFAULT_SORT: SortItem[] = [{ name: "createdAt", order: "DESC" }];
const DEFAULT_FILTERS: Partial<PurchaseOrderFilter> = {};

export const usePurchaseOrders = (options: UsePurchaseOrdersOptions = {}): UsePurchaseOrdersReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = DEFAULT_FILTERS,
    initialSort = DEFAULT_SORT,
    autoLoad = true,
    eventId = null,
  } = options;

  const { user } = useApp();
  const userId = user?.id ?? null;

  const pageSizeRef = useRef(pageSize);
  // The page size of the LAST read: a refresh re-runs that read, so a WHOLE_SET read stays whole (#118).
  const lastPageSizeRef = useRef<number>(pageSize);
  const initialFiltersRef = useRef(initialFilters);
  const initialSortRef = useRef(initialSort);
  const eventIdRef = useRef<string | null>(eventId);
  eventIdRef.current = eventId;

  const lastQueriedFilterRef = useRef<Partial<PurchaseOrderFilter>>(initialFilters);
  const lastQueriedSortRef = useRef<SortItem[]>(initialSort);

  const listCallIdRef = useRef(0);
  const previousUserIdRef = useRef<string | null | undefined>(undefined);

  const [purchaseOrder, setPurchaseOrder] = useState<PurchaseOrder | null>(null);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  /**
   * ⚠⚠ STARTS TRUE WHEN THIS HOOK IS GOING TO AUTO-LOAD — "about to read" is not "read, and empty".
   *
   * The auto-load effect is gated on `userId`, which arrives ASYNCHRONOUSLY from `AppContext`. So on
   * a hard reload or a first navigation there are renders where no request has started yet: with
   * `loading` false and the collection empty, every list component's
   * `!loading && items.length === 0` branch is TRUE and paints the first-run empty state — "No notes
   * yet", "No tasks yet" — before the skeleton and then the data. Reported across the whole event
   * space (owner, 2026-09-28).
   * ⓘ Fixed HERE rather than in the ~45 list components that read it: the lie is in the flag, not in
   * the branch, and a per-component fix would have to be remembered by every list written after it.
   * ⚠ `autoLoad: false` still starts FALSE — a hook told not to load must not claim to be loading.
   */
  const [loading, setLoading] = useState(autoLoad);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // Server total for the current filter; hasMore is derived from it (never a page-full heuristic).
  const [total, setTotal] = useState(0);

  const [filters, setFilters] = useState<Partial<PurchaseOrderFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortItem[]>(initialSort);

  const [currentPage, setCurrentPage] = useState(1);

  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set());

  const [lastError, setLastError] = useState<OpResult | null>(null);

  const isEmpty = useMemo(() => isLoaded && purchaseOrders.length === 0, [isLoaded, purchaseOrders.length]);
  const hasMore = purchaseOrders.length < total;

  const startRemoving = useCallback((ids: string[]) => {
    setRemovingIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));

      return next;
    });
  }, []);

  const stopRemoving = useCallback((ids: string[]) => {
    setRemovingIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));

      return next;
    });
  }, []);

  const isRemoving = useCallback(
    (id?: string): boolean => (id ? removingIds.has(id) : removingIds.size > 0),
    [removingIds]
  );

  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  const reset = useCallback(() => {
    listCallIdRef.current++;

    setPurchaseOrder(null);
    setPurchaseOrders([]);
    setLastError(null);
    setLoading(false);
    setIsLoaded(false);
    setLoadingMore(false);
    setTotal(0);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setRemovingIds(new Set());
  }, []);

  const get = useCallback(async (id: string): Promise<OpResult> => {
    setLoading(true);
    setLastError(null);

    try {
      const result = await PurchaseOrderService.get(id);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const data = (result.data as PurchaseOrder[])[0];
        setPurchaseOrder(data);
        setPurchaseOrders((prev) => upsertBy(prev, data));
        setLastError(null);

        return result;
      }

      setLastError(result);
      setPurchaseOrder(null);

      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(result);
      setPurchaseOrder(null);

      return result;
    } finally {
      setLoading(false);
    }
  }, []);

  const list = useCallback(
    async (
      filterOverride: Partial<PurchaseOrderFilter> = {},
      pagination?: Pagination,
      sortByOverride?: SortItem[],
      shouldReset: boolean = true
    ): Promise<OpResult> => {
      const callId = ++listCallIdRef.current;
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      lastPageSizeRef.current = targetPageSize;
      const isAppending = !shouldReset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        // A reload owns the list: a Load more it supersedes is dropped, and so is its spinner (#114).
        setLoadingMore(false);
        setCurrentPage(1);
      }

      setLastError(null);

      setFilters(filterOverride);
      lastQueriedFilterRef.current = filterOverride;

      if (sortByOverride) {
        setSortBy(sortByOverride);
        lastQueriedSortRef.current = sortByOverride;
      }

      const currentSortBy = sortByOverride || lastQueriedSortRef.current;

      try {
        const result = await listPage(
          PurchaseOrderService.list,
          {
            ...(filterOverride as PurchaseOrderFilter),
            ...(eventIdRef.current ? { contextEventId: eventIdRef.current } : {}),
          },
          { page: targetPage, pageSize: targetPageSize },
          currentSortBy
        );

        if (callId !== listCallIdRef.current) {
          return result;
        }

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newItems = Array.isArray(result.data) ? (result.data as PurchaseOrder[]) : [];

          if (isAppending) {
            setPurchaseOrders((prev) => upsertManyBy(prev, newItems, "id", "end"));
          } else {
            setPurchaseOrders(newItems);
          }

          if (typeof result.total === "number") {
            setTotal(result.total);
          } else if (!isAppending) {
            setTotal(newItems.length);
          }

          setCurrentPage(targetPage);
          setIsLoaded(true);
          setLastError(null);

          return result;
        }

        setLastError(result);
        setIsLoaded(true);

        if (!isAppending) {
          setPurchaseOrders([]);
        }

        return result;
      } catch (error) {
        if (callId !== listCallIdRef.current) {
          return OpResult.fromException(error);
        }

        const result = OpResult.fromException(error);
        setLastError(result);
        setIsLoaded(true);

        if (!isAppending) {
          setPurchaseOrders([]);
        }

        return result;
      } finally {
        if (callId === listCallIdRef.current) {
          if (isAppending) {
            setLoadingMore(false);
          } else {
            setLoading(false);
          }
        }
      }
    },
    []
  );

  const create = useCallback(async (params: PurchaseOrderInput): Promise<OpResult> => {
    setIsCreating(true);
    setLastError(null);

    try {
      const result = await PurchaseOrderService.create({
        ...params,
        ...(eventIdRef.current ? { contextEventId: eventIdRef.current } : {}),
      });

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const newItem = (result.data as PurchaseOrder[])[0];
        setPurchaseOrder(newItem);
        setPurchaseOrders((prev) => upsertBy(prev, newItem));
        setLastError(null);

        return result;
      }

      setLastError(result);

      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(result);

      return result;
    } finally {
      setIsCreating(false);
    }
  }, []);

  const update = useCallback(async (id: string, params: PurchaseOrderInput): Promise<OpResult> => {
    if (!id) {
      const result = OpResult.fail(OP_RESULT_CODES.VALIDATION_FAILED, {}, "Purchase order ID is required for updating");
      setLastError(result);

      return result;
    }

    setIsUpdating(true);
    setLastError(null);

    try {
      const result = await PurchaseOrderService.update(
        { id, ...(eventIdRef.current ? { contextEventId: eventIdRef.current } : {}) },
        { ...params, ...(eventIdRef.current ? { contextEventId: eventIdRef.current } : {}) }
      );

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const updatedItem = (result.data as PurchaseOrder[])[0];
        setPurchaseOrder(updatedItem);
        setPurchaseOrders((prev) => upsertBy(prev, updatedItem));
        setLastError(null);

        return result;
      }

      setLastError(result);

      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(result);

      return result;
    } finally {
      setIsUpdating(false);
    }
  }, []);

  const remove = useCallback(
    async (id: string): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(OP_RESULT_CODES.VALIDATION_FAILED, {}, "Purchase order ID is required for removal");
        setLastError(result);

        return result;
      }

      startRemoving([id]);
      setLastError(null);

      try {
        const result = await PurchaseOrderService.remove(id, eventIdRef.current);

        if (result.code === OP_RESULT_CODES.OK) {
          setPurchaseOrder(null);
          setPurchaseOrders((prev) => removeBy(prev, id));
          setLastError(null);

          return result;
        }

        setLastError(result);

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);

        return result;
      } finally {
        stopRemoving([id]);
      }
    },
    [startRemoving, stopRemoving]
  );

  const removeMany = useCallback(
    async (ids: string[]): Promise<OpResult> => {
      if (ids.length === 0) {
        const result = OpResult.fail(OP_RESULT_CODES.VALIDATION_FAILED, {}, "At least one order must be selected");
        setLastError(result);

        return result;
      }

      startRemoving(ids);
      setLastError(null);

      try {
        const result = await PurchaseOrderService.removeMany(ids, eventIdRef.current);

        if (result.code === OP_RESULT_CODES.OK) {
          setPurchaseOrder(null);
          setPurchaseOrders((prev) => removeManyBy(prev, ids));
          setLastError(null);

          return result;
        }

        setLastError(result);

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);

        return result;
      } finally {
        stopRemoving(ids);
      }
    },
    [startRemoving, stopRemoving]
  );

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore) {
      return;
    }

    const nextPage = currentPage + 1;

    await list(
      lastQueriedFilterRef.current,
      { page: nextPage, pageSize: pageSizeRef.current },
      lastQueriedSortRef.current,
      false
    );
  }, [list, loading, loadingMore, hasMore, currentPage]);

  const refresh = useCallback(async (): Promise<OpResult> => {
    return list(
      lastQueriedFilterRef.current,
      { page: 1, pageSize: lastPageSizeRef.current },
      lastQueriedSortRef.current,
      true
    );
  }, [list]);

  // ROADMAP #131 — a save that timed out may still have landed: re-read what is shown.
  useRefreshOnWriteTimeout(refresh, isLoaded);

  const getById = useCallback(
    (id: string | undefined): PurchaseOrder | undefined => {
      if (!id) {
        return undefined;
      }

      return purchaseOrders.find((item) => item.id === id);
    },
    [purchaseOrders]
  );

  const totalCount = total;

  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    if (previousUserId === undefined) {
      if (autoLoad && userId) {
        list(initialFiltersRef.current, { page: 1, pageSize: pageSizeRef.current }, initialSortRef.current, true);
      }

      return;
    }

    if (previousUserId !== userId) {
      reset();

      if (autoLoad && userId) {
        list(initialFiltersRef.current, { page: 1, pageSize: pageSizeRef.current }, initialSortRef.current, true);
      }
    }
  }, [userId, autoLoad, list, reset]);

  return useMemo(
    () => ({
      purchaseOrder,
      purchaseOrders,
      loading,
      isLoaded,
      isEmpty,
      loadingMore,
      hasMore,
      currentPage,
      pageSize: pageSizeRef.current,
      isCreating,
      isUpdating,
      removingIds,
      isRemoving,
      filters,
      sortBy,
      get,
      list,
      create,
      update,
      remove,
      removeMany,
      loadMore,
      refresh,
      reset,
      getById,
      totalCount,
      lastError,
      clearError,
    }),
    [
      purchaseOrder,
      purchaseOrders,
      loading,
      isLoaded,
      isEmpty,
      loadingMore,
      hasMore,
      currentPage,
      isCreating,
      isUpdating,
      removingIds,
      isRemoving,
      filters,
      sortBy,
      get,
      list,
      create,
      update,
      remove,
      removeMany,
      loadMore,
      refresh,
      reset,
      getById,
      totalCount,
      lastError,
      clearError,
    ]
  );
};
